import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useLang } from '@/context/LanguageContext';
import { useTheme } from '@/context/ThemeContext';
import {
  ArrowLeft, Globe, Sun, Moon, Phone, Lock, CheckCircle2, AlertCircle,
  Filter, Search, Heart, User, MapPin, Calendar, Sparkles, Camera, Edit3,
  ChevronDown, ChevronRight, Hash,
} from 'lucide-react';
import { motion } from 'framer-motion';
import {
  LoginDemo, FilterDemo, ViewDemo, ClaimDemo, EditDemo, LikeDemo, BrowseDemo, NavDemo, DemoSection,
} from '@/components/tutorial/TutorialDemo';

const Step = ({ n, title, children, screenshot }) => (
  <div className="glass-card rounded-2xl p-5 sm:p-6 mb-4">
    <div className="flex items-start gap-3 mb-3">
      <div className="w-9 h-9 rounded-full bg-[#D98A94] text-white flex items-center justify-center font-bold flex-shrink-0">{n}</div>
      <h3 className="t-text font-semibold text-base sm:text-lg pt-1.5">{title}</h3>
    </div>
    <div className="pl-12 space-y-3 t-text-secondary text-sm leading-relaxed">{children}</div>
    {screenshot && (
      <div className="mt-4 ml-12">
        <div className="rounded-xl overflow-hidden border-2 border-[var(--border-color)] shadow-sm">
          <img src={screenshot} alt="" className="w-full h-auto" loading="lazy" />
        </div>
      </div>
    )}
  </div>
);

const Tip = ({ children, type = 'info' }) => {
  const colors = {
    info: { bg: 'bg-[#3B82F6]/8', border: 'border-[#3B82F6]/30', icon: 'text-[#3B82F6]', Icon: Sparkles },
    warn: { bg: 'bg-[#F59E0B]/8', border: 'border-[#F59E0B]/30', icon: 'text-[#F59E0B]', Icon: AlertCircle },
    ok: { bg: 'bg-[#10B981]/8', border: 'border-[#10B981]/30', icon: 'text-[#10B981]', Icon: CheckCircle2 },
  };
  const c = colors[type];
  const Icon = c.Icon;
  return (
    <div className={`flex items-start gap-2 px-3 py-2.5 rounded-lg ${c.bg} border ${c.border}`}>
      <Icon className={`w-4 h-4 ${c.icon} flex-shrink-0 mt-0.5`} />
      <div className="t-text text-[13px] leading-relaxed">{children}</div>
    </div>
  );
};

const ClickHint = ({ icon: Icon, label, variant = 'primary' }) => {
  const variantCls = variant === 'primary'
    ? 'bg-[#D98A94] text-white'
    : variant === 'success'
    ? 'bg-[#25D366] text-white'
    : variant === 'subtle'
    ? 'glass t-text'
    : 'bg-[#C7867A] text-white';
  return (
    <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${variantCls} align-middle`}>
      {Icon && <Icon className="w-3.5 h-3.5" />}
      {label}
    </span>
  );
};

const Section = ({ id, icon: Icon, title, children, defaultOpen = false, forceOpen = false }) => {
  const [open, setOpen] = useState(defaultOpen);
  const isOpen = forceOpen || open;
  return (
    <section id={id} className="mb-6">
      <button
        onClick={() => !forceOpen && setOpen(o => !o)}
        disabled={forceOpen}
        data-testid={`tutorial-section-${id}`}
        className="w-full flex items-center justify-between p-4 glass-card rounded-2xl hover:bg-[var(--bg-input)] transition disabled:cursor-default"
      >
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#D98A94]/10 text-[#D98A94] flex items-center justify-center">
            <Icon className="w-5 h-5" />
          </div>
          <h2 className="t-text font-semibold text-base sm:text-lg text-left">{title}</h2>
        </div>
        {isOpen ? <ChevronDown className="w-5 h-5 t-text-muted" /> : <ChevronRight className="w-5 h-5 t-text-muted" />}
      </button>
      {isOpen && (
        <motion.div
          initial={forceOpen ? false : { opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          className="mt-3"
        >
          {children}
        </motion.div>
      )}
    </section>
  );
};

export default function TutorialPage() {
  const navigate = useNavigate();
  const { lang, toggleLang } = useLang();
  const { theme, toggleTheme } = useTheme();

  // EN/ID inline switch helper
  const tr = (en, id) => (lang === 'en' ? en : id);

  return (
    <div className="min-h-screen t-bg pb-20">
      {/* Top bar */}
      <div className="sticky top-0 z-50 backdrop-blur-md border-b" style={{ borderColor: 'var(--border-color)', backgroundColor: 'rgba(var(--bg-rgb,0,0,0),0.7)' }}>
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="flex items-center gap-2 t-text-secondary hover:opacity-80" data-testid="tutorial-back-btn">
            <ArrowLeft className="w-5 h-5" />
            <span className="text-sm font-medium">{tr('Back', 'Kembali')}</span>
          </button>
          <div className="flex items-center gap-2">
            <button onClick={toggleTheme} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full glass text-sm t-text-secondary">
              {theme === 'dark' ? <Sun className="w-3.5 h-3.5" /> : <Moon className="w-3.5 h-3.5" />}
            </button>
            <button onClick={toggleLang} className="flex items-center gap-1 px-2.5 py-1.5 rounded-full glass text-sm t-text-secondary">
              <Globe className="w-3.5 h-3.5" />{lang === 'en' ? 'ID' : 'EN'}
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-8">
        {/* Hero */}
        <div className="text-center mb-10">
          <div className="inline-block bg-[#D98A94]/10 text-[#D98A94] text-xs font-bold uppercase tracking-wider px-3 py-1 rounded-full mb-3">
            {tr('Step-by-step Tutorial', 'Tutorial Lengkap')}
          </div>
          <h1 className="text-3xl sm:text-4xl font-bold t-text mb-3">
            {tr('How to Use Match Group Indonesia', 'Cara Menggunakan Match Group Indonesia')}
          </h1>
          <p className="t-text-secondary text-sm sm:text-base max-w-2xl mx-auto">
            {tr(
              'Everything you need to know — log in, find members, claim your profile, and update your info. Tap any topic below to expand.',
              'Semua yang perlu Anda tahu — login, mencari anggota, klaim profil Anda, dan perbarui info. Ketuk topik di bawah untuk membuka.'
            )}
          </p>
        </div>

        {/* Quick TOC */}
        <div className="glass-card rounded-2xl p-5 mb-8">
          <p className="t-text-muted text-xs uppercase tracking-wider mb-3 font-semibold">
            {tr('In this guide', 'Dalam panduan ini')}
          </p>
          <ol className="space-y-1.5 text-sm">
            {[
              { id: 'login', label: tr('Logging in', 'Cara login') },
              { id: 'browse', label: tr('Browsing members', 'Menjelajahi anggota') },
              { id: 'filter', label: tr('Filtering by age, city, category', 'Filter berdasarkan usia, kota, kategori') },
              { id: 'view', label: tr('Viewing a member profile', 'Melihat profil anggota') },
              { id: 'like', label: tr('Liking & contacting', 'Menyukai & menghubungi') },
              { id: 'claim', label: tr('Claiming your own profile', 'Klaim profil Anda sendiri') },
              { id: 'edit', label: tr('Editing your profile after claim', 'Edit profil setelah claim') },
              { id: 'nav', label: tr('Bottom navigation', 'Navigasi bawah') },
            ].map(({ id, label }) => (
              <li key={id} className="flex items-center gap-2 t-text-secondary">
                <Hash className="w-3 h-3 t-text-muted" />
                <a href={`#${id}`} className="hover:text-[#D98A94] transition">{label}</a>
              </li>
            ))}
          </ol>
        </div>

        <TutorialSections />

        {/* Closing CTA */}
        <div className="glass-card rounded-2xl p-6 text-center mt-8 mb-8">
          <Sparkles className="w-8 h-8 text-[#F59E0B] mx-auto mb-3" />
          <h3 className="t-text font-semibold text-lg mb-2">{tr('Ready to start?', 'Siap mulai?')}</h3>
          <p className="t-text-secondary text-sm mb-4">{tr('Tap below to head straight to the login page.', 'Ketuk di bawah untuk langsung ke halaman login.')}</p>
          <button
            onClick={() => navigate('/auth')}
            data-testid="tutorial-go-login"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-[#D98A94] hover:bg-[#D98A94]/90 text-white font-medium transition active:scale-95"
          >
            <Heart className="w-4 h-4" />
            {tr('Go to Login', 'Ke Halaman Login')}
          </button>
        </div>
      </div>
    </div>
  );
}

export const TutorialSections = ({ forceOpen = false }) => {
  const { lang } = useLang();
  const tr = (en, id) => (lang === 'en' ? en : id);

  return (
    <>
      {/* 1. Login */}
      <Section id="login" icon={Lock} title={tr('1. How to Log In', '1. Cara Login')} defaultOpen forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo — watch the click sequence', 'Demo animasi — lihat urutan klik')}>
            <LoginDemo />
          </DemoSection>
          <Step n={1} title={tr('Open the login page', 'Buka halaman login')}>
            <p>{tr('Tap the "Login" button on the landing page.', 'Ketuk tombol "Login" di halaman utama.')}</p>
            <Tip type="info">{tr('You will see a form with WhatsApp number and Site Passkey.', 'Anda akan melihat form dengan Nomor WhatsApp dan Site Passkey.')}</Tip>
          </Step>

          <Step n={2} title={tr('Pick your country code', 'Pilih kode negara Anda')}>
            <p>
              {tr('Tap the country dropdown on the LEFT (default ', 'Ketuk dropdown negara di SEBELAH KIRI (default ')}
              <code className="px-1.5 py-0.5 rounded bg-[var(--bg-input)] text-xs">+62</code>
              {tr(' for Indonesia). Search by country name if needed.', ' untuk Indonesia). Cari berdasarkan nama negara jika perlu.')}
            </p>
          </Step>

          <Step n={3} title={tr('Type your WhatsApp number — NO leading 0', 'Ketik nomor WhatsApp Anda — TANPA awalan 0')}>
            <p>
              {tr('In the number field, type your number WITHOUT the leading 0. The country code on the left replaces the 0.', 'Di kolom nomor, ketik nomor Anda TANPA angka 0 di depan. Kode negara di kiri yang menggantikan angka 0 tersebut.')}
            </p>
            <Tip type="warn">
              <b>{tr('Example: ', 'Contoh: ')}</b>
              {tr('If your WhatsApp is ', 'Jika WhatsApp Anda ')}
              <code className="px-1 rounded bg-black/10">0812-3456-7890</code>,
              {tr(' type only ', ' ketik hanya ')}
              <code className="px-1 rounded bg-black/10">81234567890</code>
              {tr(' (drop the 0). The full number will be saved as ', ' (buang angka 0). Nomor lengkap akan disimpan sebagai ')}
              <code className="px-1 rounded bg-black/10">+62 812 3456 7890</code>.
            </Tip>
          </Step>

          <Step n={4} title={tr('Enter the site passkey', 'Masukkan site passkey')}>
            <p>
              {tr('Get the current passkey from the admin (it changes monthly). Paste or type it into the ', 'Dapatkan passkey terbaru dari admin (berubah setiap bulan). Tempel atau ketik di kolom ')}
              <ClickHint icon={Lock} label={tr('Site Passkey', 'Site Passkey')} variant="purple" />
              {tr(' field.', '.')}
            </p>
          </Step>

          <Step n={5} title={tr('Tap "Login" and CONFIRM your number', 'Ketuk "Login" lalu KONFIRMASI nomor Anda')}>
            <p>
              {tr('Tap ', 'Ketuk ')}
              <ClickHint icon={Heart} label={tr('Login', 'Login')} />
              .
              {tr(' A popup will show the FULL number you entered. Double-check it is correct, then tap ', ' Popup akan menampilkan nomor LENGKAP yang Anda masukkan. Periksa baik-baik, lalu ketuk ')}
              <ClickHint label={tr('Yes, log me in', 'Ya, masuk')} />
              .
              {tr(' If wrong, tap ', ' Jika salah, ketuk ')}
              <ClickHint label={tr('Edit number', 'Edit nomor')} variant="subtle" />
              .
            </p>
            <Tip type="ok">{tr('You will land on the Browse page once login succeeds.', 'Anda akan diarahkan ke halaman Browse setelah login berhasil.')}</Tip>
          </Step>
        </Section>

        {/* 2. Browse */}
        <Section id="browse" icon={Search} title={tr('2. Browsing Members', '2. Menjelajahi Anggota')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo', 'Demo animasi')}>
            <BrowseDemo />
          </DemoSection>
          <Step n={1} title={tr('Scroll through the member grid', 'Gulir grid anggota')}>
            <p>{tr('Every photo card on the Browse page is a member. Tap any card to see full details.', 'Setiap kartu foto di halaman Browse adalah anggota. Ketuk kartu untuk melihat detail lengkap.')}</p>
          </Step>
          <Step n={2} title={tr('Check "Top Liked This Month"', 'Lihat "Paling Disukai Bulan Ini"')}>
            <p>{tr('At the very top you will see the leaderboard of the most-liked profiles this month for your category. Tap any avatar to open that profile.', 'Di paling atas Anda akan melihat leaderboard profil paling banyak disukai bulan ini untuk kategori Anda. Ketuk avatar untuk membuka profil tersebut.')}</p>
          </Step>
          <Step n={3} title={tr('Check "Suitable for You"', 'Lihat "Cocok untuk Anda"')}>
            <p>{tr('Below the leaderboard you will see suggestions ranked by compatibility with your claimed profile.', 'Di bawah leaderboard ada saran yang diurutkan berdasarkan kecocokan dengan profil Anda yang sudah diklaim.')}</p>
            <Tip type="info">{tr('Picking a Category in the filter (next section) automatically narrows these suggestions to that same category.', 'Memilih Kategori di filter (bagian berikutnya) otomatis mempersempit saran ini ke kategori yang sama.')}</Tip>
          </Step>
        </Section>

        {/* 3. Filters */}
        <Section id="filter" icon={Filter} title={tr('3. Filtering by Age, City, Category', '3. Filter Berdasarkan Usia, Kota, Kategori')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo', 'Demo animasi')}>
            <FilterDemo />
          </DemoSection>
          <Step n={1} title={tr('Open the filter panel', 'Buka panel filter')}>
            <p>{tr('Tap the ', 'Ketuk tombol ')}<ClickHint icon={Filter} label={tr('Filter', 'Filter')} variant="subtle" />{tr(' button at the top right of the Browse page.', ' di kanan atas halaman Browse.')}</p>
          </Step>
          <Step n={2} title={tr('Filter by City (Domicile)', 'Filter berdasarkan Kota (Domisili)')}>
            <p>{tr('Tap the ', 'Ketuk dropdown ')}<ClickHint icon={MapPin} label={tr('Domicile', 'Domisili')} variant="subtle" />{tr(' dropdown. Pick any city/province from the list.', '. Pilih kota/provinsi dari daftar.')}</p>
          </Step>
          <Step n={3} title={tr('Filter by Category', 'Filter berdasarkan Kategori')}>
            <p>{tr('Pick e.g. ', 'Pilih misalnya ')}<ClickHint label="Buddhist Match" variant="subtle" />{tr(', ', ', ')}<ClickHint label="Catholic Match" variant="subtle" />{tr(', ', ', ')}<ClickHint label="Tenglang Match" variant="subtle" />{tr(' from the Category dropdown. The list of members AND the "Suitable for You" suggestions will both update.', ' dari dropdown Kategori. Daftar anggota DAN saran "Cocok untuk Anda" akan ikut diperbarui.')}</p>
          </Step>
          <Step n={4} title={tr('Filter by Age range', 'Filter berdasarkan rentang Usia')}>
            <p>{tr('Type a minimum and maximum age in the ', 'Ketik usia minimum dan maksimum di kolom ')}<ClickHint icon={Calendar} label={tr('Age Min / Age Max', 'Usia Min / Usia Max')} variant="subtle" />{tr(' fields (e.g. 25 to 35).', ' (misal 25 sampai 35).')}</p>
          </Step>
          <Step n={5} title={tr('Filter by Gender', 'Filter berdasarkan Gender')}>
            <p>{tr('Pick Male or Female from the Gender dropdown.', 'Pilih Pria atau Wanita dari dropdown Gender.')}</p>
          </Step>
          <Step n={6} title={tr('Clear filters', 'Hapus filter')}>
            <p>{tr('Tap ', 'Ketuk ')}<ClickHint label={tr('Clear', 'Hapus')} variant="primary" />{tr(' (red pill, top of filter panel) to reset everything.', ' (pil merah, di atas panel filter) untuk reset semuanya.')}</p>
          </Step>
        </Section>

        {/* 4. View profile */}
        <Section id="view" icon={User} title={tr('4. Viewing a Member Profile', '4. Melihat Profil Anggota')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo', 'Demo animasi')}>
            <ViewDemo />
          </DemoSection>
          <Step n={1} title={tr('Tap any photo card', 'Ketuk kartu foto')}>
            <p>{tr('A popup will open with their full info: photos, bio, occupation, education, MBTI, hobbies, etc.', 'Popup akan terbuka dengan info lengkap: foto, bio, pekerjaan, pendidikan, MBTI, hobi, dll.')}</p>
          </Step>
          <Step n={2} title={tr('Swipe through photos', 'Geser antar foto')}>
            <p>{tr('Tap the left/right arrows on the photo to switch between their photos. The photo is shown in FULL ratio so nothing is cropped.', 'Ketuk tanda panah kiri/kanan di foto untuk berpindah antar foto. Foto ditampilkan dengan rasio PENUH sehingga tidak ada yang terpotong.')}</p>
          </Step>
          <Step n={3} title={tr('Close the popup', 'Tutup popup')}>
            <p>{tr('Tap the big ', 'Ketuk tombol ')}<ClickHint label="✕" variant="subtle" />{tr(' button at the top-right of the popup. It is large to make it easy to tap on mobile.', ' besar di kanan atas popup. Ukurannya besar agar mudah diketuk di mobile.')}</p>
          </Step>
        </Section>

        {/* 5. Like & contact */}
        <Section id="like" icon={Heart} title={tr('5. Liking & Contacting', '5. Menyukai & Menghubungi')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo', 'Demo animasi')}>
            <LikeDemo />
          </DemoSection>
          <Step n={1} title={tr('Tap the heart button to LIKE', 'Ketuk tombol hati untuk LIKE')}>
            <p>{tr('Inside any member popup, tap ', 'Di dalam popup anggota, ketuk ')}<ClickHint icon={Heart} label={tr('Like', 'Like')} />{tr('. You will see their like count rise and your name will appear in their "Liked by" row.', '. Anda akan melihat jumlah like mereka bertambah dan nama Anda muncul di baris "Disukai oleh".')}</p>
            <Tip type="info">{tr('You can also see profiles you liked in the "Liked" tab at the bottom.', 'Anda juga bisa melihat profil yang sudah Anda sukai di tab "Liked" di bagian bawah.')}</Tip>
          </Step>
          <Step n={2} title={tr('Tap WhatsApp / Instagram / TikTok / Facebook to contact', 'Ketuk WhatsApp / Instagram / TikTok / Facebook untuk menghubungi')}>
            <p>
              {tr('Below the member info, you will see colored buttons: ', 'Di bawah info anggota, ada beberapa tombol berwarna: ')}
              <ClickHint icon={Phone} label="WhatsApp" variant="success" />
              {' '}
              <ClickHint icon={User} label="Instagram" variant="purple" />
              {' '}
              etc.
            </p>
            <p>{tr('Tapping any of these opens that member\'s profile directly in the corresponding app/website.', 'Ketuk salah satunya akan langsung membuka profil anggota tersebut di aplikasi/website terkait.')}</p>
            <Tip type="warn">
              <b>{tr('If the page opens to empty / "user not found":', 'Jika halaman terbuka kosong / "user tidak ditemukan":')}</b>
              {' '}
              {tr('it means the member typed their username/number wrong in their profile. Just move on to another profile.', 'berarti anggota tersebut salah memasukkan username/nomor di profil mereka. Silakan lanjut ke profil lain saja.')}
            </Tip>
          </Step>
          <Step n={3} title={tr('Note: matched profiles hide contact info', 'Catatan: profil yang sudah berjodoh menyembunyikan kontak')}>
            <p>{tr('If a member already matched with someone, you will see a big "MATCHED" stamp on their photo and their WhatsApp/Instagram buttons disappear.', 'Jika anggota sudah berjodoh, Anda akan melihat cap "MATCHED" besar di foto mereka dan tombol WhatsApp/Instagram hilang.')}</p>
          </Step>
        </Section>

        {/* 6. Claim profile */}
        <Section id="claim" icon={CheckCircle2} title={tr('6. Claiming Your Own Profile', '6. Klaim Profil Anda Sendiri')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo — full claim flow', 'Demo animasi — alur klaim lengkap')}>
            <ClaimDemo />
          </DemoSection>
          <Tip type="info">{tr('The admin creates a profile for you ahead of time. Claiming links that profile to your account so you can edit it.', 'Admin membuatkan profil untuk Anda terlebih dahulu. Klaim menghubungkan profil itu ke akun Anda agar bisa diedit.')}</Tip>

          <Step n={1} title={tr('Go to the Profile tab', 'Buka tab Profile')}>
            <p>{tr('Tap ', 'Ketuk ')}<ClickHint icon={User} label="Profile" variant="subtle" />{tr(' at the bottom of any page.', ' di bawah halaman manapun.')}</p>
          </Step>
          <Step n={2} title={tr('Tap "Claim Profile"', 'Ketuk "Claim Profile"')}>
            <p>{tr('A red button labelled ', 'Tombol merah berlabel ')}<ClickHint icon={CheckCircle2} label={tr('Claim Profile', 'Klaim Profil')} />{tr(' will appear. Tap it.', ' akan muncul. Ketuk tombol tersebut.')}</p>
          </Step>
          <Step n={3} title={tr('Wait for the progress bar', 'Tunggu progress bar')}>
            <p>{tr('A loading bar will show 0% → 100%. We fetch every page so your WhatsApp number is GUARANTEED to be found. This may take a few seconds.', 'Bar loading akan menunjukkan 0% → 100%. Kami mengambil semua halaman agar nomor WhatsApp Anda DIPASTIKAN ditemukan. Mungkin perlu beberapa detik.')}</p>
            <Tip type="ok">{tr('The screen will NOT look frozen — the bar advances live.', 'Layar TIDAK akan terlihat membeku — bar bergerak live.')}</Tip>
          </Step>
          <Step n={4} title={tr('Search by your WhatsApp number', 'Cari berdasarkan nomor WhatsApp Anda')}>
            <p>{tr('In the search box, type your WhatsApp number digits. You can type the WHOLE number (e.g. 6281234567890) or just the local part (e.g. 81234567890).', 'Di kotak pencarian, ketik digit nomor WhatsApp Anda. Boleh ketik nomor LENGKAP (mis. 6281234567890) atau bagian lokalnya saja (mis. 81234567890).')}</p>
            <Tip type="warn">{tr('WhatsApp number is the most reliable identifier. Name and city may overlap with other members.', 'Nomor WhatsApp adalah identifier paling akurat. Nama dan kota bisa sama dengan anggota lain.')}</Tip>
          </Step>
          <Step n={5} title={tr('Pick your profile from the list', 'Pilih profil Anda dari daftar')}>
            <p>{tr('Tap the row that shows your number. A red border appears around it.', 'Ketuk baris yang menampilkan nomor Anda. Garis merah akan muncul di sekelilingnya.')}</p>
          </Step>
          <Step n={6} title={tr('Tap "Claim"', 'Ketuk "Klaim"')}>
            <p>{tr('Confirm the action. Your account is now linked to that profile. You will be redirected back to your Profile page where you can see and edit your full info.', 'Konfirmasi aksi. Akun Anda sekarang terhubung ke profil tersebut. Anda akan diarahkan kembali ke halaman Profile untuk melihat dan edit info lengkap.')}</p>
          </Step>
        </Section>

        {/* 7. Edit profile */}
        <Section id="edit" icon={Edit3} title={tr('7. Editing Your Profile After Claim', '7. Edit Profil Setelah Klaim')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo', 'Demo animasi')}>
            <EditDemo />
          </DemoSection>
          <Step n={1} title={tr('Open the Profile tab', 'Buka tab Profile')}>
            <p>{tr('Tap ', 'Ketuk ')}<ClickHint icon={User} label="Profile" variant="subtle" />{tr(' at the bottom.', ' di bawah.')}</p>
          </Step>
          <Step n={2} title={tr('Tap the "Edit" button', 'Ketuk tombol "Edit"')}>
            <p>{tr('You will see a pencil-icon ', 'Anda akan melihat tombol ikon pensil ')}<ClickHint icon={Edit3} label={tr('Edit Profile', 'Edit Profil')} variant="purple" />{tr(' button. Tap it.', '. Ketuk tombol itu.')}</p>
          </Step>
          <Step n={3} title={tr('Update photos (max 3)', 'Perbarui foto (maks 3)')}>
            <p>
              {tr('In the photo grid, tap the ', 'Di grid foto, ketuk ikon ')}
              <ClickHint icon={Camera} label={tr('Camera', 'Kamera')} variant="subtle" />
              {tr(' icon on any slot. Pick a photo from your device. A crop tool will open — drag to position and tap ', ' di slot mana pun. Pilih foto dari perangkat Anda. Tool crop akan terbuka — geser untuk memposisikan dan ketuk ')}
              <ClickHint label={tr('Apply', 'Terapkan')} />
              {tr('. Files larger than 5MB are auto-compressed.', '. File lebih dari 5MB akan otomatis dikompres.')}
            </p>
            <Tip type="info">{tr('Recommended portrait orientation (3:4). Photos are displayed in their natural ratio so portraits are NOT cropped in the profile popup.', 'Disarankan orientasi portrait (3:4). Foto ditampilkan dengan rasio alami sehingga portrait TIDAK terpotong di popup profil.')}</Tip>
          </Step>
          <Step n={4} title={tr('Edit your bio & personal info', 'Edit bio & info pribadi')}>
            <p>{tr('Update fields like Name, Age, Occupation, Education, Bio, WhatsApp, Instagram, TikTok, Facebook.', 'Perbarui kolom seperti Nama, Usia, Pekerjaan, Pendidikan, Bio, WhatsApp, Instagram, TikTok, Facebook.')}</p>
            <Tip type="warn">{tr('Type Instagram/TikTok WITHOUT the @ — just the username. WhatsApp uses the country-code dropdown (no leading 0).', 'Ketik Instagram/TikTok TANPA tanda @ — hanya username. WhatsApp pakai dropdown kode negara (tanpa awalan 0).')}</Tip>
          </Step>
          <Step n={5} title={tr('Edit extended info', 'Edit info tambahan')}>
            <p>{tr('Scroll down to set MBTI, Zodiac, Shio, Religious Sect, Income, Hobby, Preferred Places, Topics to Talk, Partner Criteria, Marriage Status, Purpose.', 'Gulir ke bawah untuk mengatur MBTI, Zodiak, Shio, Aliran Agama, Pendapatan, Hobi, Tempat Favorit, Topik Pembicaraan, Kriteria Pasangan, Status Pernikahan, Tujuan.')}</p>
            <Tip type="ok">{tr('Filling more fields improves your matching score and your appearance in others\' "Suitable for You" suggestions.', 'Mengisi lebih banyak kolom meningkatkan skor kecocokan dan kemunculan Anda di "Cocok untuk Anda" milik orang lain.')}</Tip>
          </Step>
          <Step n={6} title={tr('Tap "Save"', 'Ketuk "Simpan"')}>
            <p>{tr('Hit the ', 'Tekan tombol ')}<ClickHint label={tr('Save', 'Simpan')} />{tr(' button at the bottom of the edit dialog. Your changes are saved instantly.', ' di bawah dialog edit. Perubahan tersimpan langsung.')}</p>
          </Step>
        </Section>

        {/* 8. Bottom nav */}
        <Section id="nav" icon={Sparkles} title={tr('8. Bottom Navigation', '8. Navigasi Bawah')} forceOpen={forceOpen}>
          <DemoSection label={tr('Animated demo — three tabs cycling', 'Demo animasi — tiga tab bergantian')}>
            <NavDemo />
          </DemoSection>
          <p className="t-text-secondary text-sm mb-3">{tr('Three tabs at the bottom of every page:', 'Tiga tab di bagian bawah setiap halaman:')}</p>
          <ul className="space-y-3 t-text-secondary text-sm">
            <li className="flex items-start gap-3">
              <ClickHint icon={Search} label="Browse" variant="subtle" />
              <span>{tr('See all members, suggestions, leaderboard, and filters.', 'Lihat semua anggota, saran, leaderboard, dan filter.')}</span>
            </li>
            <li className="flex items-start gap-3">
              <ClickHint icon={Heart} label="Liked" variant="primary" />
              <span>{tr('See every profile you have liked. Tap one to re-open their info.', 'Lihat setiap profil yang sudah Anda sukai. Ketuk untuk membuka info mereka kembali.')}</span>
            </li>
            <li className="flex items-start gap-3">
              <ClickHint icon={User} label="Profile" variant="purple" />
              <span>{tr('Your own profile. Claim it, edit it, or log out from here.', 'Profil Anda sendiri. Klaim, edit, atau logout dari sini.')}</span>
            </li>
          </ul>
        </Section>
    </>
  );
};
